import { DenormalizeDirections } from './types';

const getNormalizedFrameIndex = (
    newFrameIndex: number,
    framesCount: number,
    cycledMove = true,
): number => {
    const maxFrameIndex = framesCount - 1;

    let normalizedFrameIndex: number;

    if (!cycledMove) {
        let direction = Math.floor(Math.abs(newFrameIndex / framesCount));

        if (direction % 2 == 0) normalizedFrameIndex = Math.abs(newFrameIndex % framesCount);
        else normalizedFrameIndex = maxFrameIndex - Math.abs(newFrameIndex % framesCount);

    } else {
        if (newFrameIndex < 0) {
            let calculatedFrameIndex = (framesCount + newFrameIndex) % framesCount;
            normalizedFrameIndex =
                calculatedFrameIndex < 0
                    ? calculatedFrameIndex + framesCount
                    : calculatedFrameIndex;
        } else if (newFrameIndex > maxFrameIndex)
            normalizedFrameIndex = newFrameIndex % framesCount;
        else normalizedFrameIndex = newFrameIndex;
    }
    return normalizedFrameIndex;
};

const getDenormalizedFrameIndex = (
    framesCount: number,
    targetFrameIndex: number,
    direction: DenormalizeDirections,
) => targetFrameIndex + (direction === DenormalizeDirections.Up ? framesCount : -1 * framesCount);

const getFrameIndexForStartIndexSmallerBoundingFrameAndIncreasing = (
    newFrameIndex: number,
    boundingFrame: number,
    framesCount: number,
) => getNormalizedFrameIndex(Math.min(newFrameIndex, boundingFrame - 1), framesCount);

const getFrameIndexForStartIndexSmallerBoundingFrameAndDecreasing = (
    newFrameIndex: number,
    normalizedFrameIndex: number,
    boundingFrame: number,
) => (newFrameIndex >= 0 ? normalizedFrameIndex : Math.max(normalizedFrameIndex, boundingFrame));

const getFrameIndexForStartIndexBiggerBoundingFrameAndIncreasing = (
    newFrameIndex: number,
    normalizedFrameIndex: number,
    maxFrameIndex: number,
    boundingFrame: number,
    framesCount: number,
) => {
    const denormalizedFrameIndexBeforeBoundingFrame = getDenormalizedFrameIndex(
        framesCount,
        boundingFrame - 1,
        DenormalizeDirections.Up,
    );

    return newFrameIndex <= maxFrameIndex
        ? normalizedFrameIndex
        : getNormalizedFrameIndex(
              Math.min(newFrameIndex, denormalizedFrameIndexBeforeBoundingFrame),
              framesCount,
          );
};

const getFrameIndexForStartIndexBiggerBoundingFrameAndDecreasing = (
    newFrameIndex: number,
    normalizedFrameIndex: number,
    boundingFrame: number,
) =>
    newFrameIndex < boundingFrame ? boundingFrame : Math.max(normalizedFrameIndex, boundingFrame);

const getFrameIndexWithOneBoundingFrame = (
    startIndex: number,
    boundingFrame: number,
    newFrameIndex: number,
    framesCount: number,
    maxFrameIndex: number,
): number => {
    const normalizedNewFrameIndex = getNormalizedFrameIndex(newFrameIndex, framesCount);

    let resultFrameIndex: number;

    if (startIndex < boundingFrame) {
        if (newFrameIndex > startIndex) {
            resultFrameIndex = getFrameIndexForStartIndexSmallerBoundingFrameAndIncreasing(
                newFrameIndex,
                boundingFrame,
                framesCount,
            );
        } else {
            resultFrameIndex = getFrameIndexForStartIndexSmallerBoundingFrameAndDecreasing(
                newFrameIndex,
                normalizedNewFrameIndex,
                boundingFrame,
            );
        }
    } else {
        if (newFrameIndex < startIndex) {
            resultFrameIndex = getFrameIndexForStartIndexBiggerBoundingFrameAndDecreasing(
                newFrameIndex,
                normalizedNewFrameIndex,
                boundingFrame,
            );
        } else {
            resultFrameIndex = getFrameIndexForStartIndexBiggerBoundingFrameAndIncreasing(
                newFrameIndex,
                normalizedNewFrameIndex,
                maxFrameIndex,
                boundingFrame,
                framesCount,
            );
        }
    }

    return resultFrameIndex;
};

const getFrameIndexWithTwoBoundingFrames = (
    startIndex: number,
    boundingFrames: [number, number],
    newFrameIndex: number,
    framesCount: number,
    maxFrameIndex: number,
): number => {
    const normalizedNewFrameIndex = getNormalizedFrameIndex(newFrameIndex, framesCount);

    const firstBoundingFrameIndex = boundingFrames[0];
    const secondBoundingFrameIndex = boundingFrames[1];

    let resultFrameIndex: number;

    if (firstBoundingFrameIndex < secondBoundingFrameIndex) {
        if (startIndex <= firstBoundingFrameIndex) {
            if (newFrameIndex > startIndex) {
                resultFrameIndex = getFrameIndexForStartIndexSmallerBoundingFrameAndIncreasing(
                    newFrameIndex,
                    firstBoundingFrameIndex + 1,
                    framesCount,
                );
            } else {
                resultFrameIndex = getFrameIndexForStartIndexSmallerBoundingFrameAndDecreasing(
                    newFrameIndex,
                    normalizedNewFrameIndex,
                    secondBoundingFrameIndex,
                );
            }
        } else {
            if (newFrameIndex < startIndex) {
                resultFrameIndex = getFrameIndexForStartIndexBiggerBoundingFrameAndDecreasing(
                    newFrameIndex,
                    normalizedNewFrameIndex,
                    secondBoundingFrameIndex,
                );
            } else {
                resultFrameIndex = getFrameIndexForStartIndexBiggerBoundingFrameAndIncreasing(
                    newFrameIndex,
                    normalizedNewFrameIndex,
                    maxFrameIndex,
                    firstBoundingFrameIndex + 1,
                    framesCount,
                );
            }
        }
    } else {
        if (newFrameIndex < startIndex) {
            resultFrameIndex = getFrameIndexForStartIndexBiggerBoundingFrameAndDecreasing(
                newFrameIndex,
                normalizedNewFrameIndex,
                secondBoundingFrameIndex,
            );
        } else {
            resultFrameIndex = getFrameIndexForStartIndexSmallerBoundingFrameAndIncreasing(
                newFrameIndex,
                firstBoundingFrameIndex + 1,
                framesCount,
            );
        }
    }

    return resultFrameIndex;
};

const getFrameIndexFromImagePath = (imgPath: string): number => {
    const imgPathByRoutes = imgPath.split('/');
    return parseInt(imgPathByRoutes[imgPathByRoutes.length - 1].split('-')[2]);
};

export {
    getNormalizedFrameIndex,
    getFrameIndexWithOneBoundingFrame,
    getFrameIndexWithTwoBoundingFrames,
    getFrameIndexFromImagePath,
};
